/**
 * ADR-0240 Slice 2d — typed client for the agency scan-widget
 * dashboard endpoints.
 *
 *   GET   /v1/agency/widget           — read `{ widget: { public_key,
 *                                        origins, created_at,
 *                                        updated_at } } | { widget: null }`.
 *   POST  /v1/agency/widget/provision — mint `public_key` (idempotent).
 *   PUT   /v1/agency/widget/origins   — replace-set the origin list.
 *   GET   /v1/agency/widget/leads     — paged widget-lead rows.
 *
 * Discriminated-union results mirror the Clients + Reports clients.
 */

import { ApiError, type HttpClient } from './http-client.js';

export interface AgencyWidgetWire {
  readonly public_key: string;
  readonly origins: readonly string[];
  readonly created_at: string;
  readonly updated_at: string;
  /** ADR-0240 Portal v4 UX (2026-10-10) — created_at of the most
   *  recent widget lead; drives the status chip. `null` when no
   *  lead has arrived yet. */
  readonly last_scan_at: string | null;
  /** When true the scan response withholds the report URL until the
   *  visitor submits their email. */
  readonly email_capture_enabled: boolean;
}

export interface AgencyWidgetLeadWire {
  readonly lead_id: string;
  readonly domain: string;
  readonly origin: string;
  readonly report_url: string | null;
  readonly outcome: 'checked' | 'unreachable' | 'unchecked' | null;
  readonly signals_present: number | null;
  readonly signals_total: number | null;
  readonly created_at: string;
  /** ADR-0240 Slice 2d MAJOR 2 — heuristic signal of whether the
   *  submitter looked like a browser (vs curl / server). The widget
   *  POST is authenticated only by `(public_key, Origin)`, both of
   *  which are public identifiers; `non_browser` is the dashboard's
   *  "Unverified source" marker, not a hard block. */
  readonly client_signal: 'browser' | 'non_browser';
  /** Visitor email captured via the email-gate flow. `null` when the
   *  agency has email capture disabled or the visitor skipped it. */
  readonly email: string | null;
}

export type GetAgencyWidgetResult =
  | { readonly kind: 'ok'; readonly widget: AgencyWidgetWire | null }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getAgencyWidget(http: HttpClient): Promise<GetAgencyWidgetResult> {
  try {
    const res = await http.request<{ widget: AgencyWidgetWire | null }>({
      method: 'GET',
      path: '/v1/agency/widget',
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', widget: res.widget };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type ProvisionAgencyWidgetResult =
  | { readonly kind: 'ok'; readonly widget: AgencyWidgetWire; readonly minted: boolean }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function provisionAgencyWidget(
  http: HttpClient,
): Promise<ProvisionAgencyWidgetResult> {
  try {
    const res = await http.request<{ widget: AgencyWidgetWire; minted: boolean }>({
      method: 'POST',
      path: '/v1/agency/widget/provision',
      body: {},
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', widget: res.widget, minted: res.minted };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type PutAgencyWidgetOriginsResult =
  | { readonly kind: 'ok'; readonly origins: readonly string[] }
  | { readonly kind: 'invalid_origin'; readonly message: string }
  | { readonly kind: 'duplicate_origin' }
  | { readonly kind: 'too_many_origins' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function putAgencyWidgetOrigins(
  http: HttpClient,
  origins: readonly string[],
): Promise<PutAgencyWidgetOriginsResult> {
  try {
    const res = await http.request<{ origins: readonly string[] }>({
      method: 'PUT',
      path: '/v1/agency/widget/origins',
      body: { origins },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', origins: res.origins };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request') {
        const reason = err.server_reason ?? 'invalid_body';
        if (reason === 'too_many_origins') return { kind: 'too_many_origins' };
        if (reason === 'duplicate_origin') return { kind: 'duplicate_origin' };
        if (reason === 'invalid_origin') {
          return {
            kind: 'invalid_origin',
            message: 'Enter valid https origins, one per line.',
          };
        }
        return { kind: 'error', message: 'The origin list is invalid.' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type GetAgencyWidgetLeadsResult =
  | {
      readonly kind: 'ok';
      readonly leads: readonly AgencyWidgetLeadWire[];
      readonly limit: number;
      readonly offset: number;
    }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getAgencyWidgetLeads(
  http: HttpClient,
  opts: { limit?: number; offset?: number } = {},
): Promise<GetAgencyWidgetLeadsResult> {
  const q = new URLSearchParams();
  if (opts.limit !== undefined) q.set('limit', String(opts.limit));
  if (opts.offset !== undefined) q.set('offset', String(opts.offset));
  const path = `/v1/agency/widget/leads${q.toString().length > 0 ? `?${q}` : ''}`;
  try {
    const res = await http.request<{
      leads: readonly AgencyWidgetLeadWire[];
      limit: number;
      offset: number;
    }>({
      method: 'GET',
      path,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok', leads: res.leads, limit: res.limit, offset: res.offset };
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'forbidden') {
      return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/** v4 (2026-10-10) — lead delete (single). */
export type RemoveWidgetLeadResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function removeWidgetLead(
  http: HttpClient,
  lead_id: string,
): Promise<RemoveWidgetLeadResult> {
  try {
    const res = await http.request<{ ok: true }>({
      method: 'DELETE',
      path: `/v1/agency/widget/leads/${encodeURIComponent(lead_id)}`,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

/** v4 (2026-10-10) — lead bulk delete. */
export type BulkRemoveWidgetLeadsResult =
  | {
      readonly kind: 'ok';
      readonly removed_count: number;
      readonly skipped_count: number;
      readonly removed: ReadonlyArray<{ readonly lead_id: string; readonly domain: string }>;
      readonly skipped: ReadonlyArray<{ readonly lead_id: string; readonly reason: 'lead_not_found' }>;
    }
  | { readonly kind: 'empty' }
  | { readonly kind: 'too_many_rows'; readonly max: number }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function bulkRemoveWidgetLeads(
  http: HttpClient,
  lead_ids: readonly string[],
): Promise<BulkRemoveWidgetLeadsResult> {
  try {
    const res = await http.request<{
      removed_count: number;
      skipped_count: number;
      removed: ReadonlyArray<{ lead_id: string; domain: string }>;
      skipped: ReadonlyArray<{ lead_id: string; reason: 'lead_not_found' }>;
    }>({
      method: 'POST',
      path: '/v1/agency/widget/leads/bulk-delete',
      body: { lead_ids },
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return {
      kind: 'ok',
      removed_count: res.removed_count,
      skipped_count: res.skipped_count,
      removed: res.removed,
      skipped: res.skipped,
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      if (err.kind === 'bad_request') {
        if (err.server_reason === 'empty') return { kind: 'empty' };
        if (err.server_reason === 'too_many_rows') return { kind: 'too_many_rows', max: 100 };
      }
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

export type PutAgencyWidgetSettingsResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'forbidden'; readonly reason: string }
  | { readonly kind: 'error'; readonly message: string };

export async function putAgencyWidgetSettings(
  http: HttpClient,
  settings: { email_capture_enabled: boolean },
): Promise<PutAgencyWidgetSettingsResult> {
  try {
    const res = await http.request<{ ok: true }>({
      method: 'PUT',
      path: '/v1/agency/widget/settings',
      body: settings,
    });
    if (res === undefined) return { kind: 'error', message: 'Empty response body.' };
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden', reason: err.server_reason ?? 'forbidden' };
      return { kind: 'error', message: err.server_reason ?? err.kind };
    }
    return { kind: 'error', message: explainApiError(err) };
  }
}

function explainApiError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.server_reason ?? err.kind;
  }
  return err instanceof Error ? err.message : 'unknown_error';
}
